import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import VerticalBarChart from '../components/VerticalBarChart';
import { formatCurrency } from '../utils/formatCurrency';
import { useVatMode } from '../hooks/useVatMode';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import CollapsibleSection from '../components/CollapsibleSection';
import EmptyState from '../components/EmptyState';
import { apiFetch } from '../utils/api';
import { BTN } from '../constants/buttons';
import ChartTimeRange, { getChartInitialRange } from '../components/ChartTimeRange';
import { useDepartment } from '../contexts/DepartmentContext';
import { STATUS_COLORS_HEX as STATUS_COLORS, STATUS_COLORS as STATUS_COLORS_TAILWIND, STATUS_LABELS, SUPPLIER_COLORS, SUB_DEPT_COLORS } from '../constants/colors';
import { APPROVAL_ROLES } from '../constants/statuses';

const DEFAULT_WIDGETS = {
  summaryCards: true,
  statusPieChart: true,
  subDeptSpendChart: true,
  spendOverTime: true,
  supplierSpendChart: true,
  statusBreakdown: true,
};

const WIDGET_LABELS = {
  summaryCards: 'Summary Cards',
  statusPieChart: 'Order Status Chart',
  subDeptSpendChart: 'Sub Department Spend',
  spendOverTime: 'Spend Over Time',
  supplierSpendChart: 'Spend by Supplier',
  statusBreakdown: 'Status Breakdown',
};

const LS_KEY_WIDGETS = 'sfx_user_dashboard_widgets';

// Helper to build query string from date range (used by per-chart fetches only)
function buildQS(range) {
  const params = new URLSearchParams();
  if (range && range.start_date) params.set('start_date', range.start_date);
  if (range && range.end_date) params.set('end_date', range.end_date);
  const qs = params.toString();
  return qs ? '?' + qs : '';
}

function MyDashboard() {
  const navigate = useNavigate();
  const { activeDepartment, activeRole, isAdmin, isBuyer, loading: deptLoading } = useDepartment();
  const { formatMoney } = useVatMode();
  const activeDeptId = activeDepartment ? activeDepartment.department_id : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [supplierSpend, setSupplierSpend] = useState([]);
  const [spendOverTime, setSpendOverTime] = useState([]);
  const [spendPeriod, setSpendPeriod] = useState(() => {
    try { return localStorage.getItem('sfx_user_spend_period') || 'week'; } catch { return 'week'; }
  });
  const [showCustomize, setShowCustomize] = useState(false);
  const [widgets, setWidgets] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_WIDGETS);
      return saved ? { ...DEFAULT_WIDGETS, ...JSON.parse(saved) } : DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });
  const [subDeptSpend, setSubDeptSpend] = useState([]);
  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  const currentUserId = localStorage.getItem('sfx_user_id') || '';
  const userRole = activeRole || 'user';
  const subDepartment = localStorage.getItem('sfx_sub_department') || '';
  const showSubDeptSpend = APPROVAL_ROLES.includes(userRole);

  // Build user rank string for welcome message
  const getUserRank = () => {
    if (isAdmin) return 'Admin';
    if (isBuyer) return 'Buyer';
    if (activeRole === 'supervisor') return 'Supervisor';
    if (userRole === 'sub_department_lead' && subDepartment) return `${subDepartment} Lead`;
    if (subDepartment) return subDepartment;
    return '';
  };
  const userRank = getUserRank();

  // Per-chart date ranges
  // statusPieRange removed — status breakdown is always all-time (Feature #312)
  const [supplierChartRange, setSupplierChartRange] = useState(() => getChartInitialRange('user_supplier_chart'));
  const [subDeptRange, setSubDeptRange] = useState(() => getChartInitialRange('user_sub_dept'));
  const [spendOTRange, setSpendOTRange] = useState(() => getChartInitialRange('user_spend_ot'));

  // Per-chart data states
  // statusPieData removed — status breakdown always comes from main dashboard data (Feature #312)
  const [supplierChartData, setSupplierChartData] = useState(null);
  const [subDeptChartData, setSubDeptChartData] = useState(null);

  // Per-chart RANGED-fetch error flags (F6, silent-failure survey). A ranged
  // fetch failure must NEVER be answered by leaving the previous range's data
  // sitting in state under the new range's label — that is money attributed to
  // the wrong period and it looks entirely healthy. Each handler below sets its
  // flag on failure and clears it on success; the render gates on `!error`,
  // never on the data array, so a rejected/refused request always replaces the
  // chart with an explicit ErrorMessage instead of stale or falsely-empty bars.
  const [supplierChartError, setSupplierChartError] = useState(false);
  const [subDeptChartError, setSubDeptChartError] = useState(false);
  const [spendOverTimeError, setSpendOverTimeError] = useState(false);

  const fetchSpendOverTime = useCallback((range, period) => {
    if (!currentUser) return;
    const r = range || {};
    const p = period || spendPeriod;
    const params = new URLSearchParams();
    if (r.start_date) params.set('start_date', r.start_date);
    if (r.end_date) params.set('end_date', r.end_date);
    params.set('period', p);
    setSpendOverTimeError(false);
    apiFetch(`/api/dashboard/user/${currentUserId}/spend-over-time?` + params.toString())
      .then(res => {
        if (!res.ok) throw new Error('Failed to load spend-over-time data');
        return res.json();
      })
      .then(d => { setSpendOverTime(d.data || []); })
      .catch(() => { setSpendOverTime([]); setSpendOverTimeError(true); });
  }, [currentUser, spendPeriod]);

  const fetchDashboard = useCallback(() => {
    if (!currentUser || deptLoading) {
      if (!currentUser) setLoading(false);
      return;
    }

    // Only show full-page loading spinner on initial load
    // For refreshes, keep existing content visible to preserve scroll position
    if (!hasLoadedOnce.current) setLoading(true);
    setError(null);
    // No date range params — summary cards always show all-time figures
    apiFetch(`/api/dashboard/user/${currentUserId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load dashboard data');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); hasLoadedOnce.current = true; })
      .catch(err => { setError(err.message); setLoading(false); });

    apiFetch(`/api/dashboard/user/${currentUserId}/supplier-spend`)
      .then(res => res.ok ? res.json() : [])
      .then(d => setSupplierSpend(d))
      .catch(() => setSupplierSpend([]));

    // Fetch sub-department spend for admin/sub_department_lead roles
    if (APPROVAL_ROLES.includes(userRole)) {
      apiFetch('/api/dashboard/admin/sub-department-spend')
        .then(res => res.ok ? res.json() : [])
        .then(d => setSubDeptSpend(d))
        .catch(() => setSubDeptSpend([]));
    }

    fetchSpendOverTime();
  }, [currentUser, fetchSpendOverTime, activeDeptId, deptLoading]); // activeDeptId triggers re-fetch on department switch

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleSpendPeriodChange = (period) => {
    setSpendPeriod(period);
    try { localStorage.setItem('sfx_user_spend_period', period); } catch {}
    fetchSpendOverTime(spendOTRange, period);
  };

  // Per-chart time range handlers
  // handleStatusPieRangeChange removed — status pie always shows all-time data (Feature #312)

  const handleSupplierChartRangeChange = (range) => {
    setSupplierChartRange(range);
    if (!currentUser) return;
    setSupplierChartError(false);
    apiFetch(`/api/dashboard/user/${currentUserId}/supplier-spend${buildQS(range)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load supplier spend');
        return res.json();
      })
      .then(d => setSupplierChartData(d))
      .catch(() => { setSupplierChartError(true); });
  };

  const handleSubDeptRangeChange = (range) => {
    setSubDeptRange(range);
    setSubDeptChartError(false);
    apiFetch('/api/dashboard/admin/sub-department-spend' + buildQS(range))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load sub-department spend');
        return res.json();
      })
      .then(d => setSubDeptChartData(d))
      .catch(() => { setSubDeptChartError(true); });
  };


  // formatCurrency imported from utils/formatCurrency

  const getStatusColor = (status) => {
    return STATUS_COLORS_TAILWIND[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const toggleWidget = (key) => {
    const updated = { ...widgets, [key]: !widgets[key] };
    setWidgets(updated);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(updated));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(DEFAULT_WIDGETS));
  };

  // Chart data getters - use per-chart data if available, fall back to global
  const getStatusPieData = () => {
    const breakdown = (data && data.status_breakdown) || [];
    return breakdown.map(s => ({
      // `status` rides on the slice so a click links by the STATUS VALUE —
      // never by the label, which is display copy ('Partial delivery' is
      // not a status and landed My Orders on All).
      status: s.status,
      label: STATUS_LABELS[s.status] || s.status.charAt(0).toUpperCase() + s.status.slice(1),
      value: s.count,
      color: STATUS_COLORS[s.status] || '#9CA3AF'
    }));
  };

  const getSupplierBarData = () => {
    const source = supplierChartData || supplierSpend;
    const sorted = [...source].sort((a, b) => b.total_spend - a.total_spend);
    return sorted.map((s, i) => ({
      label: s.supplier_name,
      value: s.total_spend,
      color: SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]
    }));
  };

  const getSubDeptPieData = () => {
    const source = subDeptChartData || subDeptSpend;
    if (!source || source.length === 0) return [];
    return source.map((s, i) => ({
      label: s.sub_department,
      value: Number(s.total_spend) || 0,
      color: SUB_DEPT_COLORS[i % SUB_DEPT_COLORS.length]
    }));
  };

  const getSpendOverTimeData = () => {
    const source = spendOverTime;
    if (!source || source.length === 0) return [];
    return source.map((row, index) => {
      let label = row.period_key;
      if (spendPeriod === 'week' && row.period_start) {
        const d = new Date(row.period_start + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        }
      } else if (spendPeriod === 'month' && row.period_key) {
        const parts = row.period_key.split('-');
        if (parts.length === 2) {
          const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
          if (!isNaN(d.getTime())) {
            label = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
          }
        }
      }
      return {
        label,
        value: Number(row.total_spend) || 0,
        fullLabel: row.period_key,
        orderCount: row.order_count,
        isCurrent: index === source.length - 1
      };
    });
  };

  if (loading) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSpinner message="Loading dashboard..." />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
          title="Welcome to your dashboard"
          description="Submit your first order to set up your profile and start tracking your activity."
          cta={{ label: 'Create your first order', to: '/orders/new' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <ErrorMessage error={error} onRetry={fetchDashboard} title="Failed to load dashboard" />
      </div>
    );
  }

  // Check if user has no orders at all
  const hasNoOrders = data && (!data.status_breakdown || data.status_breakdown.length === 0) && !data.total_spend && !data.outstanding_count && !data.unapproved_count;

  if (hasNoOrders) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          title="No activity yet"
          description={`Hi ${currentUser}! You haven't submitted any orders yet. Create an order to start tracking your spend and activity here.`}
          cta={{ label: 'Create your first order', to: '/orders/new' }}
        />
        {/* Show sub-department spend chart even for users with no orders */}
        {showSubDeptSpend && subDeptSpend.length > 0 && (
          <CollapsibleSection id="user-sub-dept-spend" title="Spend per Sub Department" className="mt-6" data-testid="sub-dept-spend-chart">
            <div className="p-5">
              <ChartTimeRange chartId="user_sub_dept" onChange={handleSubDeptRangeChange} className="mb-3" />
              {subDeptChartError ? (
                <ErrorMessage error="Failed to load sub-department spend for the selected range" onRetry={() => handleSubDeptRangeChange(subDeptRange)} />
              ) : (
                <PieChart
                  data={getSubDeptPieData()}
                  size={220}
                  valueFormatter={formatCurrency}
                />
              )}
            </div>
          </CollapsibleSection>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-full p-2.5">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setShowCustomize(!showCustomize)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
          title="Customize dashboard"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Customize
        </button>
      </div>

      {/* Customize Panel */}
      {showCustomize && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Customize Dashboard Widgets</h3>
            <button onClick={resetWidgets} className="text-xs text-blue-600 hover:text-blue-800">
              Reset to Default
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(WIDGET_LABELS)
              .filter(([key]) => key !== 'subDeptSpendChart' || showSubDeptSpend)
              .map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={widgets[key]}
                  onChange={() => toggleWidget(key)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}


      {/* Summary Cards */}
      {widgets.summaryCards && (
        <CollapsibleSection id="user-summary-cards" title="Totals" className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4 p-3 md:p-4">
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-green-50 hover:ring-2 hover:ring-green-300 transition-all"
              onClick={() => navigate('/orders')}
              title="View all my orders"
              data-testid="total-spend-card"
            >
              <p className="text-sm text-gray-500 mb-1">Total Spend</p>
              <p className="text-xl md:text-2xl font-bold text-green-600">{formatMoney(data.total_spend)}</p>
              {data.total_spend_inc_pending > data.total_spend && (
                <p className="text-xs text-amber-500 mt-0.5">(inc. pending: {formatMoney(data.total_spend_inc_pending)})</p>
              )}
              <p className="text-xs text-green-400 mt-1">Click to view orders</p>
            </div>
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-blue-300 transition-all"
              onClick={() => navigate('/orders?status=outstanding')}
              title="View outstanding orders"
              data-testid="outstanding-orders-card"
            >
              <p className="text-sm text-gray-500 mb-1">Outstanding Orders</p>
              <p className="text-xl md:text-2xl font-bold text-blue-600">{data.outstanding_count}</p>
              <p className="text-xs text-blue-400 mt-1">Not yet delivered &mdash; click to view</p>
            </div>
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-yellow-50 hover:ring-2 hover:ring-yellow-300 transition-all"
              onClick={() => navigate('/orders?status=submitted')}
              title="View unapproved orders"
              data-testid="unapproved-orders-card"
            >
              <p className="text-sm text-gray-500 mb-1">Unapproved Orders</p>
              <p className="text-xl md:text-2xl font-bold text-yellow-600">{data.unapproved_count}</p>
              <p className="text-xs text-yellow-500 mt-1">Awaiting approval &mdash; click to view</p>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Charts Row - Pie + Supplier side by side on desktop */}
      {(widgets.statusPieChart || widgets.supplierSpendChart) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
          {/* Order Status Pie Chart */}
          {widgets.statusPieChart && (
            <CollapsibleSection id="user-status-pie" title="Order Status Distribution">
              <div className="p-5">
                <PieChart
                  data={getStatusPieData()}
                  size={200}
                  onSliceClick={(slice) => navigate(`/orders?status=${encodeURIComponent(slice.status)}`)}
                />
              </div>
            </CollapsibleSection>
          )}

          {/* Supplier Spend Bar Chart */}
          {widgets.supplierSpendChart && (
            <CollapsibleSection id="user-supplier-chart" title="My Spend by Supplier" data-testid="user-supplier-spend-chart">
              <div className="p-5">
                <ChartTimeRange chartId="user_supplier_chart" onChange={handleSupplierChartRangeChange} className="mb-3" />
                {supplierChartError ? (
                  <ErrorMessage error="Failed to load supplier spend for the selected range" onRetry={() => handleSupplierChartRangeChange(supplierChartRange)} />
                ) : (
                  <BarChart
                    data={getSupplierBarData()}
                    valuePrefix={'\u00A3'}
                    maxBars={8}
                    onBarClick={(item) => navigate(`/orders?supplier=${encodeURIComponent(item.label)}`)}
                  />
                )}
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* Spend per Sub Department Pie Chart - visible to admin & sub_department_lead */}
      {showSubDeptSpend && widgets.subDeptSpendChart && (
        <CollapsibleSection id="user-sub-dept-spend" title="Spend per Sub Department" className="mb-6" data-testid="sub-dept-spend-chart">
          <div className="p-5">
            <ChartTimeRange chartId="user_sub_dept" onChange={handleSubDeptRangeChange} className="mb-3" />
            {subDeptChartError ? (
              <ErrorMessage error="Failed to load sub-department spend for the selected range" onRetry={() => handleSubDeptRangeChange(subDeptRange)} />
            ) : (
              <PieChart
                data={getSubDeptPieData()}
                size={220}
                valueFormatter={formatCurrency}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Spend Over Time Chart */}
      {widgets.spendOverTime && (
        <CollapsibleSection id="user-spend-over-time" title="My Spend Over Time" className="mb-6" data-testid="user-spend-over-time-chart">
          <div className="p-5">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="user-spend-period-toggle">
              <span className="text-sm text-gray-500 mr-1">View by:</span>
              <button
                onClick={() => handleSpendPeriodChange('week')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  spendPeriod === 'week'
                    ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                data-testid="user-spend-period-week"
              >
                Week
              </button>
              <button
                onClick={() => handleSpendPeriodChange('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  spendPeriod === 'month'
                    ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                data-testid="user-spend-period-month"
              >
                Month
              </button>
            </div>
            {spendOverTimeError ? (
              <ErrorMessage error="Failed to load spend over time" onRetry={() => fetchSpendOverTime(null, spendPeriod)} />
            ) : (
              <VerticalBarChart
                data={getSpendOverTimeData()}
                valuePrefix={'\u00A3'}
                color="#3B82F6"
                height={280}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Status Breakdown */}
      {widgets.statusBreakdown && (
        <CollapsibleSection id="user-status-breakdown" title="Order Status Breakdown" className="mb-6">
          <div className="p-5">
            {data.status_breakdown && data.status_breakdown.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {data.status_breakdown.map(s => (
                  <button
                    key={s.status}
                    onClick={() => navigate(`/orders?status=${encodeURIComponent(s.status)}`)}
                    className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-offset-1 hover:ring-gray-400 transition ${getStatusColor(s.status)}`}
                    title={`View ${s.status} orders`}
                  >
                    {STATUS_LABELS[s.status] || s.status.charAt(0).toUpperCase() + s.status.slice(1)}: {s.count}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No orders yet</p>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

export default MyDashboard;
